import clsx, { ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

// Named spacing tokens registered in tailwind/global.css's @theme block (`--spacing-<name>`). Without
// this, tailwind-merge can't tell `gap-stack-l` is a gap, so `cn('gap-[30px]', 'gap-stack-l')` keeps both.
// cn.spec.ts fails if this list drifts from global.css.
export const NAMED_SPACING_TOKENS = [
	'body',
	'grid-gutter',
	'fluid-grid-gutter',
	'section-margin',
	'inline-2xs',
	'inline-xs',
	'inline-s',
	'inline-m',
	'inline-l',
	'inline-xl',
	'inline-2xl',
	'stack-2xs',
	'stack-xs',
	'stack-s',
	'stack-m',
	'stack-l',
	'stack-xl',
	'stack-2xl',
	'stack-3xl',
	'card-padding-xs',
	'card-padding-s',
	'card-padding-m',
	'card-padding-l',
	'card-stack-xs',
	'card-stack-s',
	'card-stack-m',
	'card-stack-l',
	'tag-padding-y',
	'tag-padding-x',
	'button-padding-x',
	'button-stack',
	'button-inline',
	'button-inline-icon',
	'heading-xs',
	'heading-s',
	'heading-m',
	'heading-l',
	'heading-xl',
	'modal-padding-x',
	'modal-padding-y',
];

// The `.text-*` typography classes in tailwind/global.css. tailwind-merge otherwise reads them as text
// colours, so `cn('text-body2 text-text-secondary')` silently dropped `text-body2`. They get their own
// group (not tailwind-merge's `font-size`, which would also strip a preceding `leading-*`), so they only
// replace each other. cn.spec.ts fails if this list drifts from global.css.
export const TYPOGRAPHY_CLASSES = [
	'h1',
	'h2',
	'h3',
	'h4',
	'subtitle1',
	'subtitle2',
	'body1',
	'body2',
	'body3',
	'caption',
	'caption2',
	'caption3',
	'detail4',
	'label',
	'button1',
	'button2',
	'overline',
	'display1',
	'display2',
	'display3',
	'contextual',
];

const twMerge = extendTailwindMerge<'typography'>({
	extend: {
		theme: { spacing: NAMED_SPACING_TOKENS },
		classGroups: { typography: [{ text: TYPOGRAPHY_CLASSES }] },
	},
});

// The cn function provides a convenient way to merge and generate CSS class names
// by utilizing the clsx and tailwind-merge libraries. It simplifies the process of
// combining class names in a React project, making it easier to manage and apply styles to components.

// This spread operator puts into an array of classes
export default function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

// EXAMPLE USAGE
// import { cn } from "@/lib/utils";

// type Props = {
//   isActive: boolean;
//   className?: string;
// };

// export default function YourComponent({ isActive, className }: Props) {
//   return (
//     <div
//       className={cn(
//         "animate-pulse h-4 w-[550px] rounded-md bg-white/5",
//         className, { "bg-white": !isActive }
//       )}
//     />
//   );
// }
